import React, { useEffect, useRef, useState } from 'react';
import { Input } from './ui/input';
import { searchCustomerContacts } from '../services/api';

/**
 * Freeform email/name input that queries the shared customer contacts
 * directory (`GET /api/customers/contacts/search`) as the user types
 * and surfaces matches in a dropdown. Picking a hit fills the input
 * with the contact's email address and calls `onPickContact` with the
 * full contact object so the caller can also capture the person's
 * first name for the greeting.
 */
export const ContactAutocomplete = ({
  value,
  onChange,
  onPickContact,
  placeholder = 'name@example.com',
  testId = 'contact-autocomplete',
  minChars = 2,
  className = '',
}) => {
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    // Debounce the API call by ~250 ms so we don't hammer the search
    // endpoint on every keystroke.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Search on the LAST token so "a@x.com, m" queries for "m".
    const raw = (value || '').split(/[,;\n]/).pop().trim();
    if (raw.length < minChars) {
      setHits([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await searchCustomerContacts(raw, 10);
        setHits(res || []);
        setActiveIdx((res || []).length > 0 ? 0 : -1);
      } catch {
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, minChars]);

  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const pick = (h) => {
    // Replace only the LAST token in the value so multi-recipient fields
    // (Cc / Bcc) work naturally — earlier addresses stay put.
    const before = (value || '').split(/[,;\n]/);
    before.pop();
    const prefix = before.length ? before.join(', ').trim() + ', ' : '';
    const next = prefix + (h.email || h.name || '');
    onChange && onChange(next);
    onPickContact && onPickContact(h);
    setOpen(false);
    setHits([]);
  };

  const onKeyDown = (e) => {
    if (!open || hits.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, hits.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pick(hits[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <Input
        value={value || ''}
        onChange={(e) => { onChange && onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        data-testid={testId}
        inputMode="email"
        autoComplete="off"
      />
      {open && (loading || hits.length > 0) && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg text-sm"
          data-testid={`${testId}-dropdown`}
        >
          {loading && (
            <div className="px-3 py-2 text-slate-500 text-xs">Searching…</div>
          )}
          {hits.map((h, i) => (
            <button
              type="button"
              key={`${h.customerId}-${h.email}-${h.name}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(h); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-2 flex items-start gap-2 border-b border-slate-100 last:border-b-0 ${
                i === activeIdx ? 'bg-slate-100' : 'hover:bg-slate-50'
              }`}
              data-testid={`${testId}-hit-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">
                  {h.name || <span className="italic text-slate-500">(no name)</span>}
                  {h.title && <span className="ml-1 text-xs text-slate-500">· {h.title}</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {h.email || '(no email)'}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {h.customerName}
                  {h.kind === 'primary' && <span className="ml-1 text-slate-600">· primary</span>}
                </div>
              </div>
            </button>
          ))}
          {!loading && hits.length === 0 && (
            <div className="px-3 py-2 text-slate-500 text-xs">No matches — the address you&#39;re typing will be used as-is.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactAutocomplete;
